import React, { useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import IconOverview from '@material-ui/icons/Dashboard';
import IconAlert from '@material-ui/icons/ReportProblemOutlined';
import IconBarChart from '@material-ui/icons/BarChart';
import IconDevices from '@material-ui/icons/Devices';
import IconImportantDevices from '@material-ui/icons/ImportantDevices';
import IconSettings from '@material-ui/icons/Settings';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HandymanIcon from '@mui/icons-material/Handyman';
import InsightsIcon from '@mui/icons-material/Insights';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import IconHelp from '@material-ui/icons/Help';
import { ReactComponent as IconNBC } from '../../assets/svg/iconNoBarcode.svg';
import AppMenuItem from './SideBarMenuItem';
import { GetString } from '../StringResourceManager';
import ViewListIcon from '@mui/icons-material/ViewList';
import TimelineIcon from '@mui/icons-material/Timeline';
import InputIcon from '@mui/icons-material/Input';
import TuneIcon from '@mui/icons-material/Tune';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import BuildIcon from '@mui/icons-material/Build';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { AccessControl } from '../../Interfaces/AccessControl';
import { LocaleInterface, locales } from '../AlertsGrid/GridLocale';
import NotesIcon from '@mui/icons-material/Notes';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import MemoryIcon from '@mui/icons-material/Memory';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import HistoryIcon from '@mui/icons-material/History';
import GroupIcon from '@mui/icons-material/Group';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ScheduleIcon from '@mui/icons-material/Schedule';

// Styles
const useStyles = makeStyles(theme =>
  createStyles({
    appMenu: {
      width: '107%',
    },
  }),
);

interface Props {
  drawerOpened: boolean;
}

const SideBarMenu: React.FC<Props> = ({ drawerOpened }) => {
  const classes = useStyles();
  const PAGE_TITLE_OVERVIEW = GetString('PAGE_TITLE_OVERVIEW');
  const PAGE_TITLE_ALERTS = GetString('PAGE_TITLE_ALERTS');
  const PAGE_TITLE_ASSETS = GetString('PAGE_TITLE_ASSETS');
  const PAGE_TITLE_PRODUCTS = GetString('PAGE_TITLE_PRODUCTS');
  const PAGE_TITLE_PRODUCTS_IMPORT = GetString('PAGE_TITLE_PRODUCTS_IMPORT');
  const PAGE_TITLE_PRODUCTS_HEALTH = GetString('PAGE_TITLE_PRODUCTS_HEALTH');
  const PAGE_TITLE_REPORTS = GetString('PAGE_TITLE_REPORTS');
  const PAGE_TITLE_HEALTH = GetString('PAGE_TITLE_HEALTH');
  const PAGE_TITLE_NBC = GetString('PAGE_TITLE_NBC');
  const PAGE_TITLE_DEVICES = GetString('PAGE_TITLE_DEVICES');
  const PAGE_TITLE_TICKETS = GetString('PAGE_TITLE_TICKETS');
  const PAGE_TITLE_CONFIG = GetString('PAGE_TITLE_CONFIG');
  const PAGE_TITLE_HELP = GetString('PAGE_TITLE_HELP');
  const MENU_NAME_DEVICES_CURRENT = GetString('MENU_NAME_DEVICES_CURRENT');
  const MENU_NAME_ALERTS_CURRENT = GetString('MENU_NAME_ALERTS_CURRENT');
  const MENU_NAME_DEVICE_TRENDS = GetString('MENU_NAME_DEVICE_TRENDS');
  const MENU_NAME_DEVICE_FIRMWARE = GetString('MENU_NAME_DEVICE_FIRMWARE');
  const PAGE_TITLE_COMMANDS = GetString('PAGE_TITLE_COMMANDS');
  const PAGE_TITLE_SETTINGS = GetString('PAGE_TITLE_SETTINGS');
  const PAGE_TITLE_VISION = GetString('PAGE_TITLE_VISION');
  const PAGE_TITLE_FEATURES = GetString('PAGE_TITLE_FEATURES');
  const PAGE_TITLE_HISTORY = GetString('PAGE_TITLE_HISTORY');
  const PAGE_TITLE_ITEMS = GetString('PAGE_TITLE_ITEMS');
  const PAGE_TITLE_MAINTENANCE = GetString('PAGE_TITLE_MAINTENANCE');
  const PAGE_TITLE_MAINTENANCE_PREV = GetString('PAGE_TITLE_MAINTENANCE_PREV');
  const PAGE_TITLE_MAINTENANCE_PRED = GetString('PAGE_TITLE_MAINTENANCE_PRED');
  const PAGE_TITLE_LOGS = GetString('PAGE_TITLE_LOGS');
  const PAGE_TITLE_HISTORICAL_LOGS = GetString('PAGE_TITLE_HISTORICAL_LOGS');
  const MENU_TITLE_SCHEDULED = GetString('MENU_TITLE_SCHEDULED');
  const PAGE_TITLE_NOTIFICATIONS = GetString('PAGE_TITLE_NOTIFICATIONS');
  const ACCESS_CONTROL = GetString('ACCESS_CONTROL');
  const PAGE_TITLE_USERS = GetString('PAGE_TITLE_USERS');
  const PAGE_TITLE_DRILLDOWN = GetString('PAGE_TITLE_DRILLDOWN');

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const [loading, setLoading] = React.useState<boolean>(true);

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const accessControl: AccessControl | undefined = useSelector(
    (state: ApplicationState) =>
      state.userSessionReducer.sessionInfo?.AccessControl,
  );

  const [appMenuItems, setAppMenuItems] = useState<any[]>([]);

  useEffect((): ReturnType<React.EffectCallback> => {
    let menuItems: any[] = [];

    menuItems.push({
      name: PAGE_TITLE_OVERVIEW,
      link: '/overview',
      Icon: IconOverview,
      tooltipTitle: 'Overview',
    });

    if (accessControl?.Assets?.ShowMenu ?? false) {
      menuItems.push({
        name: PAGE_TITLE_ASSETS,
        link: '/assets',
        Icon: IconImportantDevices,
        tooltipTitle: 'Assets',
      });
    }

    if (
      (accessControl?.ProductsImport?.ShowMenu ?? false) ||
      (accessControl?.ProductsHealth?.ShowMenu ?? false)
    ) {
      let productsMenu: any = {
        name: PAGE_TITLE_PRODUCTS,
        Icon: IconDevices,
        tooltipTitle: 'Products',
        items: [],
      };
      if (accessControl?.ProductsImport?.ShowMenu ?? false) {
        productsMenu.items.push({
          name: PAGE_TITLE_PRODUCTS_IMPORT,
          link: '/productsImport',
          Icon: IconImportantDevices,
          tooltipTitle: 'Products Import',
        });
      }

      if (accessControl?.ProductsHealth?.ShowMenu ?? false) {
        productsMenu.items.push({
          name: PAGE_TITLE_PRODUCTS_HEALTH,
          link: '/productsHealth',
          Icon: MonitorHeartIcon,
          tooltipTitle: 'Products Health',
        });
      }
      menuItems.push(productsMenu);
    }

    if (
      (accessControl?.Devices?.ShowMenu ?? false) ||
      (accessControl?.Trends?.ShowMenu ?? false) ||
      (accessControl?.Firmware?.ShowMenu ?? false)
    ) {
      let devicesMenu: any = {
        name: PAGE_TITLE_DEVICES,
        Icon: IconDevices,
        tooltipTitle: 'Devices',
        items: [],
      };

      if (accessControl?.Devices?.ShowMenu ?? false) {
        devicesMenu.items.push({
          name: MENU_NAME_DEVICES_CURRENT,
          link: '/devices',
          Icon: ViewListIcon,
          tooltipTitle: 'Statuses',
        });
      }

      if (accessControl?.Trends?.ShowMenu ?? false) {
        devicesMenu.items.push({
          name: MENU_NAME_DEVICE_TRENDS,
          link: '/trends',
          Icon: TimelineIcon,
          tooltipTitle: 'Trends',
        });
      }

      if (accessControl?.Firmware?.ShowMenu ?? false) {
        devicesMenu.items.push({
          name: MENU_NAME_DEVICE_FIRMWARE,
          link: '/firmware',
          Icon: MemoryIcon,
          tooltipTitle: 'Firmware',
        });
      }

      menuItems.push(devicesMenu);
    }

    if (
      accessControl?.PreventiveMaintenance?.ShowMenu ??
      (true || accessControl?.PredictiveMaintenance?.ShowMenu) ??
      true
    ) {
      let menu: any = {
        name: PAGE_TITLE_MAINTENANCE,
        Icon: BuildIcon,
        tooltipTitle: 'Maintenance',
        items: [],
      };

      if (accessControl?.PreventiveMaintenance?.ShowMenu ?? false) {
        menu.items.push({
          name: PAGE_TITLE_MAINTENANCE_PREV,
          link: '/prevmaint',
          Icon: HandymanIcon,
          tooltipTitle: PAGE_TITLE_MAINTENANCE_PREV,
        });
      }

      if (accessControl?.PredictiveMaintenance?.ShowMenu ?? false) {
        menu.items.push({
          name: PAGE_TITLE_MAINTENANCE_PRED,
          link: '/predmaint',
          Icon: InsightsIcon,
          tooltipTitle: PAGE_TITLE_MAINTENANCE_PRED,
        });

        menuItems.push(menu);
      }
    }

    if (accessControl?.Health?.ShowMenu ?? false) {
      menuItems.push({
        name: PAGE_TITLE_HEALTH,
        link: '/health',
        Icon: MonitorHeartIcon,
        tooltipTitle: 'Health',
      });
    }

    if (accessControl?.Reports?.ShowMenu ?? false) {
      menuItems.push({
        name: PAGE_TITLE_REPORTS,
        link: '/reports',
        Icon: IconBarChart,
        tooltipTitle: 'Reports',
      });
    }

    if (
      (accessControl?.Alerts?.ShowMenu ?? false) ||
      (accessControl?.Commands?.ShowMenu ?? false) ||
      (accessControl?.AlertsScheduled?.ShowMenu ?? false)
    ) {
      let alertsMenu: any = {
        name: PAGE_TITLE_ALERTS,
        Icon: IconAlert,
        tooltipTitle: 'Alerts',
        items: [],
      };

      if (accessControl?.Alerts?.ShowMenu ?? false) {
        alertsMenu.items.push({
          name: MENU_NAME_ALERTS_CURRENT,
          link: '/alerts',
          Icon: ViewListIcon,
          tooltipTitle: 'Alerts',
        });
      }

      if (accessControl?.Commands?.ShowMenu ?? false) {
        alertsMenu.items.push({
          name: PAGE_TITLE_COMMANDS,
          link: '/commands',
          Icon: InputIcon,
          tooltipTitle: 'Commands',
        });
      }

      if (accessControl?.AlertsScheduled?.ShowMenu ?? false) {
        alertsMenu.items.push({
          name: MENU_TITLE_SCHEDULED,
          link: '/scheduledWeightImports',
          Icon: ScheduleIcon,
          tooltipTitle: MENU_TITLE_SCHEDULED,
        });
      }

      menuItems.push(alertsMenu);
    }

    if (accessControl?.Tickets?.ShowMenu ?? false) {
      menuItems.push({
        name: PAGE_TITLE_TICKETS,
        link: '/tickets',
        Icon: LocalOfferIcon,
        tooltipTitle: 'Tickets',
      });
    }

    if (
      (accessControl?.NBCItems?.ShowMenu ?? false) ||
      (accessControl?.NBCDrilldown?.ShowMenu ?? false)
    ) {
      let nbcMenu: any = {
        name: PAGE_TITLE_NBC,
        Icon: IconNBC,
        tooltipTitle: 'NBC',
        items: [],
      };

      if (accessControl?.NBCItems?.ShowMenu ?? false) {
        nbcMenu.items.push({
          name: PAGE_TITLE_ITEMS,
          link: '/itemsSubMenu',
          Icon: ShoppingBagIcon,
          tooltipTitle: 'NBC Items',
        });
      }

      if (accessControl?.NBCDrilldown?.ShowMenu ?? false) {
        nbcMenu.items.push({
          name: PAGE_TITLE_DRILLDOWN,
          link: '/drilldownSubMenu',
          Icon: AccountTreeIcon,
          tooltipTitle: 'NBC Drilldown',
        });
      }

      if (accessControl?.NBCHistory?.ShowMenu ?? false) {
        nbcMenu.items.push({
          name: PAGE_TITLE_HISTORY,
          link: '/nbcHistory',
          Icon: TimelineIcon,
          tooltipTitle: 'History Sub-Menu',
        });
      }

      menuItems.push(nbcMenu);
    }

    if (
      (accessControl?.ConfigJsonSettings?.ShowMenu ?? false) ||
      (accessControl?.ConfigSettings?.ShowMenu ?? false) ||
      (accessControl?.ConfigFeatures?.ShowMenu ?? false)
    ) {
      let menu: any = {
        name: PAGE_TITLE_CONFIG,
        Icon: IconSettings,
        tooltipTitle: 'Config',
        items: [],
      };

      if (accessControl?.ConfigSettings?.ShowMenu ?? false) {
        menu.items.push({
          name: PAGE_TITLE_SETTINGS,
          link: '/settingsSubMenu',
          Icon: TuneIcon,
          tooltipTitle: 'Settings Sub-Menu',
        });
      }

      if (accessControl?.ConfigJsonSettings?.ShowMenu ?? false) {
        menu.items.push({
          name: PAGE_TITLE_VISION,
          link: '/visionSubMenu',
          Icon: TuneIcon,
          tooltipTitle: 'Settings Sub-Menu',
        });
      }

      if (accessControl?.ConfigFeatures?.ShowMenu ?? false) {
        menu.items.push({
          name: PAGE_TITLE_FEATURES,
          link: '/featuresSubMenu',
          Icon: SettingsSuggestIcon,
          tooltipTitle: 'Features Sub-Menu',
        });
      }

      if (accessControl?.ConfigHistory?.ShowMenu ?? false) {
        menu.items.push({
          name: PAGE_TITLE_HISTORY,
          link: '/configHistory',
          Icon: TimelineIcon,
          tooltipTitle: 'History Sub-Menu',
        });
      }

      menuItems.push(menu);
    }

    if (accessControl?.Notifications?.ShowMenu ?? false) {
      menuItems.push({
        name: PAGE_TITLE_NOTIFICATIONS,
        link: '/notifications',
        Icon: NotificationAddIcon,
        tooltipTitle: 'Notifications Menu',
      });
    }

    if (
      accessControl?.HistoricalLogs?.ShowMenu ??
      (true || accessControl?.ScheduledLogs?.ShowMenu) ??
      true
    ) {
      let menu: any = {
        name: PAGE_TITLE_LOGS,
        Icon: NotesIcon,
        tooltipTitle: 'Logs',
        items: [],
      };

      if (accessControl?.HistoricalLogs?.ShowMenu ?? false) {
        menu.items.push({
          name: PAGE_TITLE_HISTORICAL_LOGS,
          link: '/historicalLogs',
          Icon: HistoryIcon,
          tooltipTitle: PAGE_TITLE_HISTORICAL_LOGS,
        });
      }

      if (accessControl?.ScheduledLogs?.ShowMenu ?? false) {
        menu.items.push({
          name: MENU_TITLE_SCHEDULED,
          link: '/scheduledLogs',
          Icon: ScheduleIcon,
          tooltipTitle: MENU_TITLE_SCHEDULED,
        });

        menuItems.push(menu);
      }
    }

    if (
      (accessControl?.AccessControlMenu?.ShowMenu ?? false) ||
      (accessControl?.AccessControlHistory.ShowMenu ?? false) ||
      (accessControl?.AccessControlScheduled.ShowMenu ?? false)
    ) {
      let accessControlMenu: any = {
        name: ACCESS_CONTROL,
        Icon: LockPersonIcon,
        tooltipTitle: 'AccessControl',
        items: [],
      };

      if (accessControl?.AccessControlMenu.ShowMenu ?? false) {
        accessControlMenu.items.push({
          name: PAGE_TITLE_USERS,
          link: '/accessControl',
          Icon: GroupIcon,
          tooltipTitle: 'Access Control',
        });
      }

      if (accessControl?.AccessControlScheduled.ShowMenu ?? false) {
        accessControlMenu.items.push({
          name: MENU_TITLE_SCHEDULED,
          link: '/scheduledConfigAccessControl',
          Icon: ScheduleIcon,
          tooltipTitle: MENU_TITLE_SCHEDULED,
        });
      }

      if (accessControl?.AccessControlHistory.ShowMenu ?? false) {
        accessControlMenu.items.push({
          name: PAGE_TITLE_HISTORY,
          link: '/accessControlHistory',
          Icon: HistoryIcon,
          tooltipTitle: 'History',
        });
      }

      menuItems.push(accessControlMenu);
    }

    if (accessControl?.Help?.ShowMenu ?? false) {
      menuItems.push({
        name: PAGE_TITLE_HELP,
        link: '/help',
        Icon: IconHelp,
        tooltipTitle: 'Help',
      });
    }

    setAppMenuItems(menuItems);
    //return (): void => {};
  }, [accessControl, selectedLanguage]);

  return (
    <List component="nav" className={classes.appMenu} disablePadding>
      {appMenuItems.map((item, index) => (
        <AppMenuItem {...item} key={index} drawerOpened={drawerOpened} />
      ))}
    </List>
  );
};

export default SideBarMenu;
